import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../shared/api';

// ── Icons per result type ─────────────────────────────────────────────────────

const TYPE_ICONS = {
  project: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M3 4l18 0"/><path d="M4 4v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-10"/>
      <path d="M12 16l0 4"/><path d="M9 20l6 0"/><path d="M8 12l3 -3l2 2l3 -3"/>
    </svg>
  ),
  contract: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
      <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/>
      <path d="M9 9l1 0"/><path d="M9 13l6 0"/><path d="M9 17l6 0"/>
    </svg>
  ),
  customer: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/>
      <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/>
    </svg>
  ),
  crew: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"/>
      <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1"/>
      <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"/>
      <path d="M17 10h2a2 2 0 0 1 2 2v1"/>
      <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"/>
      <path d="M3 13v-1a2 2 0 0 1 2 -2h2"/>
    </svg>
  ),
  transport: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M5 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
      <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
      <path d="M7 18h8m4 0h2v-6a5 7 0 0 0 -5 -7h-1l1.5 7h4.5"/>
      <path d="M12 18v-13h3"/><path d="M3 17l0 -5l9 0"/>
    </svg>
  ),
  item: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M7 16.5l-5 -3l5 -3l5 3v5.5l-5 3z"/>
      <path d="M2 13.5v5.5l5 3"/><path d="M7 16.545l5 -3.03"/>
      <path d="M17 16.5l-5 -3l5 -3l5 3v5.5l-5 3z"/>
      <path d="M12 19l5 3"/><path d="M17 16.5l5 -3"/>
      <path d="M12 13.5v-5.5l-5 -3l5 -3l5 3v5.5"/>
    </svg>
  ),
  product: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M7 16.5l-5 -3l5 -3l5 3v5.5l-5 3z"/>
      <path d="M2 13.5v5.5l5 3"/><path d="M7 16.545l5 -3.03"/>
      <path d="M17 16.5l-5 -3l5 -3l5 3v5.5l-5 3z"/>
      <path d="M12 19l5 3"/><path d="M17 16.5l5 -3"/>
      <path d="M12 13.5v-5.5l-5 -3l5 -3l5 3v5.5"/>
    </svg>
  ),
  rental: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M9.615 20h-2.615a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8"/>
      <path d="M14 19l2 2l4 -4"/><path d="M9 8h4"/><path d="M9 12h2"/>
    </svg>
  ),
};

const TYPE_COLORS = {
  project: 'bg-blue-lt text-blue',
  contract: 'bg-teal-lt text-teal',
  customer: 'bg-cyan-lt text-cyan',
  crew: 'bg-purple-lt text-purple',
  transport: 'bg-orange-lt text-orange',
  item: 'bg-green-lt text-green',
  product: 'bg-green-lt text-green',
  rental: 'bg-yellow-lt text-yellow',
};

// ── Debounce hook ─────────────────────────────────────────────────────────────

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// ── Component ─────────────────────────────────────────────────────────────────

const GlobalSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // ── Fetch results ───────────────────────────────────────────────────────────

  const { data: results = [], isFetching: loading } = useQuery({
    queryKey: ['globalSearch', debouncedQuery],
    queryFn: () => api.get(`search/?q=${encodeURIComponent(debouncedQuery)}`),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      setOpen(true);
      setActiveIndex(-1);
    }
  }, [results, debouncedQuery]);

  // ── Close on outside click ──────────────────────────────────────────────────

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Keyboard navigation ─────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e) => {
    if (!open) return;
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        navigateTo(results[activeIndex].url);
      }
    }
  }, [open, results, activeIndex]);

  // ── Navigate to result ──────────────────────────────────────────────────────

  const navigateTo = useCallback((url) => {
    setOpen(false);
    setQuery('');
    navigate(url);
  }, [navigate]);

  // ── Group results by type ───────────────────────────────────────────────────

  const grouped = results.reduce((acc, item, idx) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push({ ...item, _idx: idx });
    return acc;
  }, {});

  const typeOrder = ['project', 'contract', 'customer', 'crew', 'transport', 'product', 'item', 'rental'];
  const orderedTypes = typeOrder.filter((t) => grouped[t]);

  // ── Render ──────────────────────────────────────────────────────────────────

  const showDropdown = open && query.length >= 2;

  return (
    <div
      ref={containerRef}
      className="global-search-wrapper"
      style={{ position: 'relative', width: '100%' }}
    >
      <form onSubmit={(e) => e.preventDefault()} autoComplete="off" noValidate>
        <div className="input-icon">
          <span className="input-icon-addon">
            {loading ? (
              <span
                className="spinner-border spinner-border-sm text-secondary"
                style={{ width: '1rem', height: '1rem' }}
              />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"/>
                <path d="M21 21l-6 -6"/>
              </svg>
            )}
          </span>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            placeholder={t('components.globalSearch.placeholder')}
            aria-label={t('components.globalSearch.placeholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value.length >= 2) setOpen(true);
              else setOpen(false);
            }}
            onFocus={() => {
              if (query.length >= 2 && results.length > 0) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
          />
          {query && (
            <button
              type="button"
              className="input-icon-addon"
              onClick={() => { setQuery(''); setOpen(false); }}
              style={{
                left: 'auto',
                right: 0,
                pointerEvents: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--tblr-secondary)',
              }}
              aria-label="Clear"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6l-12 12"/><path d="M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
      </form>

      {showDropdown && (
        <div
          className="global-search-dropdown card shadow-lg"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 9999,
            maxHeight: '480px',
            overflowY: 'auto',
            border: '1px solid var(--tblr-border-color)',
            borderRadius: 'var(--tblr-border-radius)',
            background: 'var(--tblr-bg-surface)',
          }}
        >
          {results.length === 0 && !loading && (
            <div className="p-3 text-muted text-center small">
              {t('components.globalSearch.noResults', { query })}
            </div>
          )}

          {orderedTypes.map((type) => (
            <div key={type}>
              <div
                className="px-3 pt-2 pb-1"
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--tblr-secondary)',
                  borderTop: '1px solid var(--tblr-border-color)',
                }}
              >
                {t(`components.globalSearch.types.${type}`)}
              </div>
              {grouped[type].map((item) => (
                <div
                  key={`${type}-${item.id}`}
                  className={`global-search-item d-flex align-items-center gap-2 px-3 py-2${activeIndex === item._idx ? ' active' : ''}`}
                  style={{
                    cursor: 'pointer',
                    background: activeIndex === item._idx ? 'var(--tblr-active-bg, rgba(32,107,196,.06))' : 'transparent',
                  }}
                  onMouseEnter={() => setActiveIndex(item._idx)}
                  onMouseLeave={() => setActiveIndex(-1)}
                  onClick={() => navigateTo(item.url)}
                >
                  <span
                    className={`avatar avatar-xs rounded ${TYPE_COLORS[type] || 'bg-secondary-lt text-secondary'}`}
                    style={{ flexShrink: 0, width: '1.75rem', height: '1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {TYPE_ICONS[type]}
                  </span>
                  <div className="flex-grow-1 overflow-hidden">
                    <div
                      className="fw-medium"
                      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}
                    >
                      <Highlight text={item.title} query={query} />
                    </div>
                    {item.subtitle && (
                      <div
                        className="text-muted"
                        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}
                      >
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Highlight matched text ────────────────────────────────────────────────────

function Highlight({ text, query }) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ padding: 0, background: 'rgba(255,193,7,.35)', borderRadius: '2px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default GlobalSearch;
